import { Observable, share } from 'rxjs';
import { type ServiceTokens } from '../../container';
import { type RequestUploadUrlInput } from '../graphQlTypes.generated';
import { api } from './operations.generated';
import { type AppStore } from '../../stores/redux';
import { type CommonRealtimeService } from '../call/realtime/commonRealtime.service';
import {
  type FileTransferEventPayload,
  type FileAvailableMessagePayload,
} from '../../features/fileTransfer/fileTransfer.type';
import {
  type RealtimeMessage,
  RealtimeMessageType,
} from '../../features/realtime/realtime.types';

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

type FileHostingServiceDependencies = {
  [ServiceTokens.ReduxStore]: AppStore;
  [ServiceTokens.CommonRealtime]: CommonRealtimeService;
};

type CreateUploadUrlParams = Optional<
  RequestUploadUrlInput,
  'userId' | 'realtimeMemberId' | 'ttl'
>;

export class FileHostingService {
  readonly availableFile: Observable<FileAvailableMessagePayload>;

  private readonly redux: AppStore;

  private readonly realtime: CommonRealtimeService;

  constructor({ reduxStore, commonRealtime }: FileHostingServiceDependencies) {
    this.redux = reduxStore;
    this.realtime = commonRealtime;

    this.availableFile = new Observable<FileAvailableMessagePayload>(
      (observer) => {
        const unsubscribe = this.realtime.on(
          RealtimeMessageType.FileTransferRequest,
          (message: RealtimeMessage<FileTransferEventPayload>) => {
            const eventData = message.payload?.eventData;
            if (eventData && 'fileReady' in eventData) {
              observer.next(eventData);
            }
          },
        );

        return () => {
          unsubscribe();
        };
      },
    ).pipe(share());
  }

  async createSession(providerId: string): Promise<string> {
    const response = await this.redux.dispatch(
      api.endpoints.createFileTransferSession.initiate({
        input: { providerId },
      }),
    );

    if ('error' in response) {
      throw new Error('File Transfer Session could not be created');
    }

    return response.data.FileTransfer.createFileTransferSession;
  }

  async createUploadUrl({
    sessionId,
    fileName,
    contentType,
    ttl = 3600, // 1 hour by default
    userId = this.realtime.getIdentity().userId as string,
    realtimeMemberId = this.realtime.getIdentity().id || userId,
  }: CreateUploadUrlParams): Promise<string> {
    const response = await this.redux.dispatch(
      api.endpoints.requestFileTransferUpload.initiate({
        input: {
          sessionId,
          contentType,
          fileName,
          ttl,
          userId,
          realtimeMemberId,
        },
      }),
    );

    if ('error' in response) {
      throw new Error('File Transfer Upload URL could not be created');
    }

    return response.data.FileTransfer.requestFileTransferUpload.uploadLink;
  }

  async upload(url: string, data: File): Promise<void> {
    const response = await fetch(url, {
      method: 'PUT',
      body: data,
      headers: {
        'Content-Type': data.type,
      },
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }
  }

  async download(url: string): Promise<Buffer> {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Download failed');
    }

    return Buffer.from(await response.arrayBuffer());
  }
}
